import { useCallback, useEffect } from 'react';
import * as L from 'leaflet';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

// Import all our hooks (using optimized versions where available)
import { useMapInteractions } from './useMapInteractions';
import { usePlaceData } from './usePlaceData';
import { useDropPointOperations } from './useDropPointOperations';
import { useNotifications } from './useNotifications';
import { useSelectionState } from './useSelectionState';
import { useStatePersistence } from './useStatePersistence';
import { usePerformanceMonitoring } from './usePerformanceMonitoring';

// Import types
import { Place, DropPoint } from '@m4a3/api-client';
import { normalizeDropPoints } from '../../../utils/cellDropPointUtils';
import { logToDatadog } from '../../../services/datadog';
import { StatusType } from '@datadog/browser-logs';
import { orderfulApi } from '../../../services/api-instances';

// Import Redux actions and selectors for cell management
import { getCells, setSelectedCell } from '../../../actions/orderActions';
import { selectCells, selectSelectedCellId } from '../../../selectors/ordersSelector';
import { cellNameCoords } from '../../../constants';

/**
 * FIXED master hook - removed performance dependency from useEffect to prevent infinite renders
 */
export const useDropPointManagement = () => {
  // Initialize Redux
  const dispatch = useDispatch();
  const cells = useSelector(selectCells);
  const selectedCellId = useSelector(selectSelectedCellId);

  // Initialize all sub-hooks
  const { persistentState, updateMapPosition, updateLastSelectedPlace } = useStatePersistence();
  const performance = usePerformanceMonitoring();
  
  const mapInteractions = useMapInteractions();
  const placeData = usePlaceData(); // This now uses the optimized version with caching
  const dropPointOps = useDropPointOperations();
  const notifications = useNotifications();
  const selection = useSelectionState();

  // Load cells on initialization
  useEffect(() => {
    dispatch(getCells());
  }, [dispatch]);

  // Initialize map from persisted state
  useEffect(() => {
    performance.mark('component-init-start');
    if (persistentState.mapCenter && persistentState.mapZoom) {
      mapInteractions.setMapCenter(persistentState.mapCenter);
      mapInteractions.setMapZoom(persistentState.mapZoom);
    }
    performance.mark('component-init-end');
    performance.measure('component-initialization', 'component-init-start', 'component-init-end');
  }, []);

  // Component initialization with performance tracking
  useEffect(() => {
    logToDatadog('drop_point_management_initialized', { 
      component: 'DropPointManagement',
      cacheStats: placeData.getCacheStats(),
    });
    performance.markUserInteraction('component_mounted');
  }, []);

  // Load places on initial render and when map moves
  useEffect(() => {
    const loadPlaces = async () => {
      const center = L.latLng(mapInteractions.mapCenter[0], mapInteractions.mapCenter[1]);
      try {
        await performance.measureApiCall(
          () => placeData.loadPlacesInViewport(center, mapInteractions.mapZoom),
          'loadPlacesInViewport'
        );
      } catch (error) {
        await placeData.loadPlacesInViewport(center, mapInteractions.mapZoom);
      }
    };
    loadPlaces().catch(err => {
      console.error('Initial loadPlaces failed:', err);
    });
  }, [placeData.loadPlacesInViewport, mapInteractions.mapCenter, mapInteractions.mapZoom]);

  // Get coordinates for cell
  const getCellCoordinates = useCallback((cellName: string) => {
    const coords = cellNameCoords[cellName as keyof typeof cellNameCoords];
    return coords ? [coords[0], coords[1]] as [number, number] : null;
  }, []);

  // Handle cell selection
  const handleCellChange = useCallback((cellId: number) => {
    const cell = cells.find(c => c.id === cellId);
    if (cell) {
      // Save to localStorage like OrderHub does
      localStorage.setItem('selectedCell', cellId.toString());
      
      // Update Redux state
      dispatch(setSelectedCell(cellId, false)); // false for useWebSocketForOrders
      
      // Update map center based on cell coordinates
      const coords = getCellCoordinates(cell.cellName);
      if (coords) {
        mapInteractions.setMapCenter(coords);
        mapInteractions.setMapZoom(15); // Good zoom level for drop point management
        
        // Clear current selection when switching cells
        selection.clearSelection();
        
        logToDatadog('cell_changed', { 
          cellId, 
          cellName: cell.cellName, 
          coordinates: coords 
        });
      }
    }
  }, [cells, dispatch, getCellCoordinates, mapInteractions, selection]);

  // Initialize cell selection from localStorage
  useEffect(() => {
    if (cells && cells.length > 0 && !selectedCellId) {
      try {
        const storedCellId = localStorage.getItem('selectedCell');
        if (storedCellId) {
          const cellId = parseInt(storedCellId);
          if (cells.find(c => c.id === cellId)) {
            handleCellChange(cellId);
            return;
          }
        }
        
        // Default to Blanchardstown
        const blanchardstownCell = cells.find(c => c.cellName.toLowerCase().includes('blanchardstown'));
        if (blanchardstownCell) {
          handleCellChange(blanchardstownCell.id);
        } else if (cells[0]) {
          // Fallback to first cell
          handleCellChange(cells[0].id);
        }
      } catch (err) {
        console.error('Error setting default cell:', err);
        logToDatadog('cell_initialization_error', { error: err }, StatusType.error);
      }
    }
  }, [cells, selectedCellId, handleCellChange]);

  // Handle map movement with persistence and performance tracking
  const handleMapMove = useCallback((center: L.LatLng, zoom: number) => {
    try {
      performance.startRenderMeasurement();
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    const newCenter: [number, number] = [center.lat, center.lng];
    
    mapInteractions.handleMapMove(
      center,
      zoom,
      async (c, z) => {
        await placeData.loadPlacesInViewport(c, z);
        mapInteractions.forceRefreshPlaces();
        mapInteractions.setMapCenter([c.lat, c.lng]);
        mapInteractions.setMapZoom(z);
      },
      () => {
        selection.clearSelection();
        mapInteractions.enterSelectMode();
        dropPointOps.cancelPinPlacement();
      }
    );
    
    // Persist map position
    updateMapPosition(newCenter, zoom);
    
    try {
      performance.endRenderMeasurement('handleMapMove');
      performance.markUserInteraction('map_moved', { center: newCenter, zoom });
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
  }, [
    mapInteractions.handleMapMove,
    placeData.loadPlacesInViewport,
    selection.clearSelection,
    mapInteractions.enterSelectMode,
    dropPointOps.cancelPinPlacement,
    updateMapPosition,
    mapInteractions.forceRefreshPlaces,
    // REMOVED: performance dependency
  ]);

  // Handle zoom operations without clearing selection
  const handleMapZoom = useCallback((center: L.LatLng, zoom: number) => {
    try {
      performance.startRenderMeasurement();
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    const newCenter: [number, number] = [center.lat, center.lng];
    
    // Use zoom-specific logic that preserves selection
    mapInteractions.handleMapMove(
      center,
      zoom,
      async (c, z) => {
        await placeData.loadPlacesInViewport(c, z);
        mapInteractions.forceRefreshPlaces();
      },
      undefined // Don't clear selection on zoom
    );
    
    // Persist map position
    updateMapPosition(newCenter, zoom);
    
    try {
      performance.endRenderMeasurement('handleMapZoom');
      performance.markUserInteraction('map_zoomed', { center: newCenter, zoom });
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
  }, [
    mapInteractions.handleMapMove,
    placeData.loadPlacesInViewport,
    updateMapPosition,
    mapInteractions.forceRefreshPlaces,
    // REMOVED: performance dependency
  ]);

  // Handle place selection with performance monitoring
  const handlePlaceClick = useCallback(async (place: Place) => {
    try {
      performance.markUserInteraction('place_clicked', { placeId: place.id });
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    try {
      mapInteractions.enterSelectMode();
      
      // Clear any existing risk assessment from previous selection
      dropPointOps.clearRiskAssessment();
      
      let context;
      try {
        context = await performance.measureApiCall(
          () => placeData.loadPlaceContext(place),
          'loadPlaceContext'
        );
      } catch (e) {
        // Fallback without performance monitoring
        context = await placeData.loadPlaceContext(place);
      }
      
      selection.selectPlace(place, context);
      dropPointOps.updateDropPointsFromContext(context);
      
      // Persist last selected place
      updateLastSelectedPlace(place.id);
      
      logToDatadog('place_selected_success', { 
        placeId: place.id,
        dropPointCount: normalizeDropPoints(context.drop_points).length,
        cacheStats: placeData.getCacheStats(),
      });
    } catch (err) {
      console.error('Error in handlePlaceClick:', err);
      notifications.showError('Failed to load place details');
      logToDatadog('place_select_failed', { placeId: place.id, error: err }, StatusType.error);
    }
  }, [
    mapInteractions.enterSelectMode,
    dropPointOps.clearRiskAssessment,
    placeData.loadPlaceContext,
    placeData.getCacheStats,
    selection.selectPlace,
    dropPointOps.updateDropPointsFromContext,
    updateLastSelectedPlace,
    notifications.showError,
    // REMOVED: performance dependency
  ]);

  // Handle drop point selection with performance tracking
  const handleDropPointClick = useCallback(async (dropPoint: DropPoint) => {
    if (dropPoint.isDeleted) return;
    
    try {
      performance.markUserInteraction('drop_point_clicked', { dropPointId: dropPoint.id });
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    selection.selectDropPoint(dropPoint);
    try {
      try {
        await performance.measureApiCall(
          () => dropPointOps.evaluateDropPoint(dropPoint),
          'evaluateDropPoint'
        );
      } catch (e) {
        // Fallback without performance monitoring
        await dropPointOps.evaluateDropPoint(dropPoint);
      }
    } catch (error) {
      console.error('Error evaluating drop point:', error);
      // Don't show error to user for evaluation failure - it's not critical
    }
  }, [selection.selectDropPoint, dropPointOps.evaluateDropPoint]); // REMOVED: performance dependency

  // Handle map click for adding drop points
  const handleMapClick = useCallback((coordinates: L.LatLng) => {
    if (mapInteractions.clickMode === 'add-drop-point' && selection.selectedPlaceContext) {
      try {
        performance.markUserInteraction('map_clicked_add_pin', { 
          coordinates: [coordinates.lat, coordinates.lng] 
        });
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
      dropPointOps.handlePinMove(coordinates);
    }
  }, [mapInteractions.clickMode, selection.selectedPlaceContext, dropPointOps.handlePinMove]); // REMOVED: performance

  // Handle starting pin add mode
  const handleAddPin = useCallback(async () => {
    if (!selection.selectedPlaceContext) return;
    
    try {
      performance.markUserInteraction('add_pin_started');
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    try {
      mapInteractions.enterAddDropPointMode();
      await dropPointOps.startAddingPin(selection.selectedPlaceContext, mapInteractions.mapCenter);
    } catch (error) {
      console.error('Error starting pin add mode:', error);
      notifications.showError('Failed to start pin placement mode');
    }
  }, [
    selection.selectedPlaceContext,
    mapInteractions.enterAddDropPointMode,
    dropPointOps.startAddingPin,
    mapInteractions.mapCenter,
    notifications.showError,
    // REMOVED: performance dependency
  ]);

  // Handle saving new pin with performance tracking
  const handleSavePin = useCallback(async () => {
    if (!selection.selectedPlaceContext || !dropPointOps.newPinLocation || !dropPointOps.riskAssessment || !dropPointOps.riskAssessment.canSave) {
      return;
    }

    try {
      performance.markUserInteraction('save_pin_started');
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }

    try {
      try {
        await performance.measureApiCall(
          () => dropPointOps.createNewDropPoint(
            selection.selectedPlaceContext!,
            dropPointOps.newPinLocation!,
            dropPointOps.riskAssessment!
          ),
          'createNewDropPoint'
        );
      } catch (e) {
        // Fallback without performance monitoring
        await dropPointOps.createNewDropPoint(
          selection.selectedPlaceContext!,
          dropPointOps.newPinLocation!,
          dropPointOps.riskAssessment!
        );
      }
      
      // Refresh the drop points for this place
      if (selection.selectedPlace) {
        let context;
        try {
          context = await performance.measureApiCall(
            () => placeData.refreshPlaceContext(selection.selectedPlace!.id),
            'refreshPlaceContext'
          );
        } catch (e) {
          // Fallback without performance monitoring
          context = await placeData.refreshPlaceContext(selection.selectedPlace!.id);
        }
        selection.updatePlaceContext(context);
        dropPointOps.updateDropPointsFromContext(context);
      }
      
      // Reset pin placement state
      flushSync(() => {
        dropPointOps.cancelPinPlacement();
        mapInteractions.enterSelectMode();
      });
      
      notifications.showSuccess('Drop point created successfully!');
      try {
        performance.markUserInteraction('save_pin_completed');
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
    } catch (error) {
      console.error('Error saving drop point:', error);
      notifications.showError('Failed to create drop point');
      try {
        performance.markUserInteraction('save_pin_failed');
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
    }
  }, [
    selection.selectedPlaceContext,
    selection.selectedPlace,
    selection.updatePlaceContext,
    dropPointOps.newPinLocation,
    dropPointOps.riskAssessment,
    dropPointOps.createNewDropPoint,
    dropPointOps.updateDropPointsFromContext,
    dropPointOps.cancelPinPlacement,
    placeData.refreshPlaceContext,
    mapInteractions.enterSelectMode,
    notifications.showSuccess,
    notifications.showError,
    // REMOVED: performance dependency
  ]);

  // Handle canceling pin placement
  const handleCancelNewPin = useCallback(() => {
    try {
      performance.markUserInteraction('cancel_pin');
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    dropPointOps.cancelPinPlacement();
    mapInteractions.enterSelectMode();
  }, [dropPointOps.cancelPinPlacement, mapInteractions.enterSelectMode]); // REMOVED: performance

  // Handle drop point deletion with performance tracking
  const handleDropPointDelete = useCallback(async (dropPoint: DropPoint) => {
    try {
      performance.markUserInteraction('delete_drop_point_started', { dropPointId: dropPoint.id });
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    try {
      try {
        await performance.measureApiCall(
          () => dropPointOps.deleteExistingDropPoint(dropPoint),
          'deleteExistingDropPoint'
        );
      } catch (e) {
        // Fallback without performance monitoring
        await dropPointOps.deleteExistingDropPoint(dropPoint);
      }
      
      // Refresh the place context
      if (selection.selectedPlace) {
        let context;
        try {
          context = await performance.measureApiCall(
            () => placeData.refreshPlaceContext(selection.selectedPlace!.id),
            'refreshPlaceContext'
          );
        } catch (e) {
          // Fallback without performance monitoring
          context = await placeData.refreshPlaceContext(selection.selectedPlace!.id);
        }
        selection.updatePlaceContext(context);
        dropPointOps.updateDropPointsFromContext(context);
      }
      
      // Clear selection if this was the selected drop point
      if (selection.selectedDropPointId === dropPoint.id) {
        selection.clearDropPointSelection();
      }
      
      notifications.showSuccess('Drop point deleted successfully!');
      try {
        performance.markUserInteraction('delete_drop_point_completed');
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
    } catch (error) {
      console.error('Error deleting drop point:', error);
      notifications.showError('Failed to delete drop point');
      try {
        performance.markUserInteraction('delete_drop_point_failed');
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
    }
  }, [
    dropPointOps.deleteExistingDropPoint,
    dropPointOps.updateDropPointsFromContext,
    selection.selectedPlace,
    selection.selectedDropPointId,
    selection.updatePlaceContext,
    selection.clearDropPointSelection,
    placeData.refreshPlaceContext,
    notifications.showSuccess,
    notifications.showError,
    // REMOVED: performance dependency
  ]);

  // Handle note creation
  const handleNoteCreated = useCallback(async () => {
    try {
      performance.markUserInteraction('note_created');
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    try {
      if (selection.selectedPlace) {
        let context;
        try {
          context = await performance.measureApiCall(
            () => placeData.refreshPlaceContext(selection.selectedPlace!.id),
            'refreshPlaceContext'
          );
        } catch (e) {
          // Fallback without performance monitoring
          context = await placeData.refreshPlaceContext(selection.selectedPlace!.id);
        }
        selection.updatePlaceContext(context);
      }
    } catch (error) {
      console.error('Error refreshing place context after note creation:', error);
    }
  }, [selection.selectedPlace, selection.updatePlaceContext, placeData.refreshPlaceContext]); // REMOVED: performance

  // Handle place status update (enable/disable)
  const handlePlaceStatusUpdate = useCallback(async (placeId: string, enabled: boolean) => {
    try {
      await placeData.updatePlaceStatus(placeId, enabled);
      notifications.showSuccess(`Place ${enabled ? 'enabled' : 'disabled'} successfully`);
      
      // Refresh the place context if this place is currently selected
      if (selection.selectedPlace?.id === placeId) {
        const context = await placeData.refreshPlaceContext(placeId);
        selection.updatePlaceContext(context);
      }
      
      logToDatadog('place_status_update_success', { 
        placeId, 
        enabled,
        action: enabled ? 'enabled' : 'disabled'
      });
    } catch (err) {
      console.error('Error updating place status:', err);
      notifications.showError(`Failed to ${enabled ? 'enable' : 'disable'} place`);
      logToDatadog('place_status_update_failed', { placeId, enabled, error: err }, StatusType.error);
    }
  }, [
    placeData.updatePlaceStatus,
    placeData.refreshPlaceContext,
    selection.selectedPlace?.id,
    selection.updatePlaceContext,
    notifications.showSuccess,
    notifications.showError,
  ]);

  // Handle setting drop point as preferred
  const handleDropPointSetPreferred = useCallback(async (dropPoint: DropPoint) => {
    try {
      performance.markUserInteraction('set_preferred_drop_point_started', { dropPointId: dropPoint.id });
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    
    try {
      if (!selection.selectedPlace) {
        notifications.showError('No place selected');
        return;
      }

      // Update place with preferred drop point ID
      try {
        await performance.measureApiCall(
          () => orderfulApi.updatePlaceContext(selection.selectedPlace!.id, { 
            enabled: true,
            preferredDropPointId: dropPoint.id 
          }),
          'setPreferredDropPoint'
        );
      } catch (e) {
        // Fallback without performance monitoring
        await orderfulApi.updatePlaceContext(selection.selectedPlace!.id, { 
          enabled: true,
          preferredDropPointId: dropPoint.id 
        });
      }
      
      // Refresh the place context to get updated data
      let context;
      try {
        context = await performance.measureApiCall(
          () => placeData.refreshPlaceContext(selection.selectedPlace!.id),
          'refreshPlaceContext'
        );
      } catch (e) {
        // Fallback without performance monitoring
        context = await placeData.refreshPlaceContext(selection.selectedPlace!.id);
      }
      
      selection.updatePlaceContext(context);
      dropPointOps.updateDropPointsFromContext(context);
      
      notifications.showSuccess('Drop point set as preferred successfully!');
      try {
        performance.markUserInteraction('set_preferred_drop_point_completed');
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
      
      logToDatadog('preferred_drop_point_set_success', {
        placeId: selection.selectedPlace.id,
        dropPointId: dropPoint.id,
        coordinates: { lat: dropPoint.latitude, lng: dropPoint.longitude }
      });
      
    } catch (error) {
      console.error('Error setting preferred delivery spot:', error);
      notifications.showError('Failed to set preferred delivery spot');
      try {
        performance.markUserInteraction('set_preferred_drop_point_failed');
      } catch (e) {
        // Silently fail if performance monitoring has issues
      }
      logToDatadog('preferred_drop_point_set_failed', {
        placeId: selection.selectedPlace?.id,
        dropPointId: dropPoint.id,
        error
      }, StatusType.error);
    }
  }, [
    selection.selectedPlace,
    selection.updatePlaceContext,
    placeData.refreshPlaceContext,
    dropPointOps.updateDropPointsFromContext,
    notifications.showSuccess,
    notifications.showError,
    // REMOVED: performance dependency
  ]);

  // Handle closing/canceling everything
  const handleClose = useCallback(() => {
    try {
      performance.markUserInteraction('close_panel');
    } catch (e) {
      // Silently fail if performance monitoring has issues
    }
    mapInteractions.enterSelectMode();
    dropPointOps.cancelPinPlacement();
    selection.clearSelection();
    updateLastSelectedPlace(null);
  }, [
    mapInteractions.enterSelectMode,
    dropPointOps.cancelPinPlacement,
    selection.clearSelection,
    updateLastSelectedPlace,
    // REMOVED: performance dependency
  ]);

  // Get performance metrics for debugging (wrapped in try-catch)
  const getPerformanceMetrics = useCallback(() => {
    try {
      return {
        cacheStats: placeData.getCacheStats(),
        cacheHitRate: performance.getCacheHitRate(),
        memoryUsage: performance.getMemoryUsage(),
      };
    } catch (error) {
      console.warn('Performance metrics failed:', error);
      return {
        cacheStats: placeData.getCacheStats(),
        cacheHitRate: 0,
        memoryUsage: undefined,
      };
    }
  }, [placeData.getCacheStats]); // REMOVED: performance dependencies

  // Return consolidated state and actions
  return {
    // State
    state: {
      // Map state
      mapCenter: mapInteractions.mapCenter,
      mapZoom: mapInteractions.mapZoom,
      clickMode: mapInteractions.clickMode,
      placesUpdateKey: mapInteractions.placesUpdateKey,
      
      // Places state
      places: placeData.places,
      placeStatuses: placeData.placeStatuses,
      placesLoading: placeData.loading,
      
      // Selection state
      selectedPlace: selection.selectedPlace,
      selectedPlaceContext: selection.selectedPlaceContext,
      selectedPlaceId: selection.selectedPlaceId,
      selectedDropPoint: selection.selectedDropPoint,
      selectedDropPointId: selection.selectedDropPointId,
      hasPlaceSelected: selection.hasPlaceSelected,
      hasDropPointSelected: selection.hasDropPointSelected,
      
      // Drop point operations state
      dropPoints: dropPointOps.dropPoints,
      newPinLocation: dropPointOps.newPinLocation,
      riskAssessment: dropPointOps.riskAssessment,
      isLoadingRiskAssessment: dropPointOps.isLoadingRiskAssessment,
      
      // Notifications
      notification: notifications.notification,
      
      // Cell state
      cells,
      selectedCellId,
    },
    
    // Actions
    actions: {
      // Map actions
      handleMapMove,
      handleMapZoom,
      handleMapClick,
      handlePlaceClick,
      handleDropPointClick,
      
      // Pin management
      handleAddPin,
      handleSavePin,
      handleCancelNewPin,
      handlePinMove: dropPointOps.handlePinMove,
      
      // Drop point actions
      handleDropPointDelete,
      handleDropPointEvaluate: dropPointOps.evaluateDropPoint,
      handleDropPointSelect: selection.selectDropPoint,
      handleDropPointSetPreferred,
      
      // Place actions
      handlePlaceStatusUpdate,
      
      // Other actions
      handleNoteCreated,
      handleClose,
      
      // Notifications
      hideNotification: notifications.hideNotification,
      
      // Performance & debugging
      getPerformanceMetrics,
      clearCache: placeData.clearCache,
      
      // Cell actions
      handleCellChange,
    },
  };
};
